import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertService } from '@service/alert.service';
import { AuthService } from '@service/auth.service';
import { BrokrageService } from '@service/brokrage.service';
import { DialogService } from '@service/dialog.service';
import { finalize, Subject, takeUntil, timer } from 'rxjs';
import { TradingService } from '../trading.service';
import { environment } from 'src/environments/environment';
import { quantityLotSizeValidator } from './lotSizeValidator';

@Component({
  selector: 'app-trade-call-dialog',
  templateUrl: './trade-call-dialog.component.html',
  styleUrls: ['./trade-call-dialog.component.scss']
})
export class TradeCallDialogComponent implements OnInit, OnDestroy {

  callType: string;
  tradeCallForm: FormGroup
  userBroker: string;
  isBrokerValid: boolean = false
  currentUser: any;
  _unsubscribeAll = new Subject()
  brokrageList: any[] = []
  @ViewChild('loginTemplate') brokersTemplate: TemplateRef<any>
  isBrokerLoading: boolean = false
  isTradeLoading: boolean = false
  isManualEntry: boolean = true
  isMarketOpen:boolean = false;
  venturaResponse:any;
  constructor(
    private dialogRef: MatDialogRef<TradeCallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { call: any, packId: any, tradeData: any, isExit: boolean },
    private fb: FormBuilder,
    private authService: AuthService,
    private brokerageService: BrokrageService,
    private alertService: AlertService,
    private tradingService: TradingService,
    private dialogService: DialogService,
    private router: Router
  ) { }

  ngOnInit(): void {    
    if (this.data) {
      this.initForm()
    }
    this.authService.userProfile.subscribe((res) => {
      this.currentUser = res
      this.userBroker = this.currentUser.brokerageOptions[0]
    })
    this.alertService.getBrokerageHouses().pipe(takeUntil(this._unsubscribeAll)).subscribe((res: any) => {
      this.brokrageList = res || [];
    })
    this.isBrokerValid = this.brokerageService.checkBrokerStatus(this.currentUser)
    if (!this.isBrokerValid) {
      window.localStorage.setItem('tradeCall', JSON.stringify({ call: this.data.call._id, scriptType: this.data.call.scriptType }))
    } else {
      this.isManualEntry = false
    }
    this.checkMarketIsOpen()
  }

  checkMarketIsOpen(){
    this.tradingService.marketHours().subscribe({
      next:(res:any)=>{
        this.isMarketOpen = res.status;
      }
    })
  }
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null)
    this._unsubscribeAll.complete()
  }

  close() {
    this.dialogRef.close()
  }

  initForm() {
    this.tradeCallForm = this.fb.group({
      isExternal: [false, Validators.required],
      complexty: ['REGULAR', Validators.required],
      orderType: ["LIMIT", Validators.required],
      quantity: [this.data.call?.lotSize || this.data.call.quantity, Validators.required],
      disclosedQuantity: [0, Validators.required],
      price: [this.data.call.currentPrice, Validators.compose([
        Validators.required,
        this.entryPriceValidator.bind(this)
      ])],
      triggerPrice: [0, Validators.required],
      validity: ["DAY", Validators.required],
      product: [this.data.call.callCategory === 'cash'? "CNC": "NRML", Validators.required],
      stopLoss: [this.data.call.stopLoss],
      target: [this.data.call.targetOne],
      trailingStopLoss: [null],
      transactionType: ['TRADE', Validators.required]
    });
    if (this.data.isExit) {
      this.tradeCallForm.controls['quantity'].patchValue(this.data.tradeData?.tradeQuantity || this.data.call.quantity);
      this.tradeCallForm.controls['transactionType'].patchValue("EXIT");
      this.tradeCallForm.controls.quantity.disable();
    }
    if(this.data.call.callCategory === 'future' || this.data.call.callCategory === 'options'){
      this.tradeCallForm.controls['quantity'].setValidators([Validators.required,quantityLotSizeValidator(this.data.call?.lotSize)])
    }
    this.tradeCallForm.get('orderType').valueChanges.subscribe((res)=> {
      res === 'MARKET'? this.tradeCallForm.get('price').disable() : this.tradeCallForm.get('price').enable() 
    })
    this.orderConditions()
  }

  brokerLogin() {
    if(!((this.data.call.callCategory === 'future' || this.data.call.callCategory === 'options') && (this.userBroker !== 'zerodha' && this.userBroker !== 'nuvama'))){
      if (this.userBroker) {
        if (this.isBrokerValid) {
          this.isManualEntry = false
        } else {
          this.brokerageService.gotoBrokerLogin(this.userBroker)
        }
      } else {
        this.dialogService.openTemplateDialog(this.brokersTemplate, {
          width: window.innerWidth > 767 ? '50%' : '100%',
          height: 'auto',
          panelClass: ['bg-dialog-color-202028', 'border-radius-dialog-16', 'no-padding'],
          id: 'broker-logins'
        })
      }
    }
  }

  closeBrokersDialog() {
    this.dialogService.closeDialogbyId('broker-logins')
  }

  tradeCall(isExternal = false) {
    if(
      this.tradeCallForm.valid &&
      !(
        (this.data.call.callCategory === 'future' || this.data.call.callCategory === 'options') &&
        (
          (this.userBroker !== 'zerodha' && this.userBroker !== 'nuvama' && isExternal === false)
        )
      )
    ) {
      this.tradeCallForm.controls['isExternal'].patchValue(isExternal)
      const data = { ...this.tradeCallForm.getRawValue(),packId: this.data?.packId }
      this.isTradeLoading = true        
      this.tradingService.tradeCall(this.data.call._id, data,this.data?.packId).subscribe({
        next: (res) => {
          this.venturaResponse = res
          if (this.userBroker === 'zerodha' && !this.tradeCallForm.get('isExternal').value) {
            this.orderInZerodha(res.data)
          }
          else if (this.userBroker === 'upstox' && !this.tradeCallForm.get('isExternal').value) {
            if (!this.brokerageService.checkBrokerStatus(this.currentUser)) {
              this.brokerageService.gotoBrokerLogin('upstox')
            }
            else {
              this.orderInUpstox(res.data)
            }
          }else if(this.userBroker === 'ventura' && !this.tradeCallForm.get('isExternal').value){
            this.getVenturaOrders(res.data)
          } else {
            this.alertService.success(res.message, true)
            timer(5000).subscribe(() => {
              this.isTradeLoading = false
            });
    
            if (this.data.isExit) {
              this.tradingService.saveCallActivity({ callId: this.data.call._id, status: 'EXITED' })
            } else {
              this.tradingService.saveCallActivity({ callId: this.data.call._id, status: 'TRADED' })
            }
    
            let data = {
              type: this.tradeCallForm.get('isExternal').value ? 'external' : 'broker',
              orderId: res.data._id
            }
            this.dialogRef.close(data)
          }
    },
        error: (err) => {
          this.isTradeLoading = false
          this.brokerageService.handleRelogin(err, this.currentUser)
        }
      })
    }
  }

  orderInZerodha(orderData) {
    const call = { ...this.tradeCallForm.value }
    const newInstrument = {
      tradingsymbol: orderData.tradingSymbol,
      exchange: orderData.exchange,
      variety: 'regular',
      transaction_type: this.data.isExit ? 'SELL' : orderData.scriptType,
      order_type: call.orderType,
      product: orderData.tradeProductType,
      price: orderData.purchasePrice,
      quantity: orderData.quantity,
      readonly: false,
      tag: orderData.zerodhaTag,
    };
    let orders: any[] = [newInstrument];
    let currOrders: any = {
      orderId: orderData._id,
      type: 'trading'
    }
    window.localStorage.setItem('tradingOrder', JSON.stringify(currOrders))
    this.brokerageService.zerodhaOrderPlace(orders)
  }
  orderInUpstox(orderData) {
    const call = { ...this.tradeCallForm.value }
    let newInstrument: any = {
      is_amo: false,
      instrument_token: orderData.upstoxInstrumentKey,
      validity: 'DAY',
      transaction_type: this.data.isExit ? 'SELL' : orderData.scriptType,
      order_type: call.orderType,
      price: orderData.purchasePrice,
      quantity: orderData.quantity,
      product: call.product == "CNC" ? 'D' : 'I',
      tag: orderData.upstoxTag,
    };
    if (call.orderType == "MARKET") {
      delete newInstrument.price;
    }

    let orders: any[] = [newInstrument];
    let currOrders: any = {
      orderId: orderData._id,
      type: 'trading'
    }
    window.localStorage.setItem('tradingOrder', JSON.stringify(currOrders))
    // this.brokerageService.placeUpstoxOrders(orders).subscribe((res) => { });
    this.brokerageService.upstoxOrderPlace(orders, this.currentUser.upstox.accessToken, `/trading/ideas/${this.data.call._id}`)
  }
  selectBroker(broker) {
    const data = {
      brokerageOptions: [broker.code]
    };
    this.isBrokerLoading = true
    this.tradingService.updateBrokerage(data).pipe(finalize(() => this.isBrokerLoading = false)).subscribe(
      (res: any) => {
        this.authService.UpdateUserData(false);
        this.brokerageService.gotoBrokerLogin(broker.code)
      }
    );
  }

  orderConditions() {
    // if (this.strategicCall) {
    //   for (const calls of this.data.call.calls) {
    //     const strategicOrderForm = this.initalizeStrategyForm();
    //     if (
    //       this.data.user.brokerageOptions.indexOf("master_trust") > -1
    //     ) {
    //       strategicOrderForm.controls.product.patchValue("NRML");
    //     } else {
    //       if (calls.callCategory === "cash") {
    //         if (this.data.call.label === "intraday") {
    //           strategicOrderForm.controls.product.patchValue("MIS");
    //         } else {
    //           strategicOrderForm.controls.product.patchValue("CNC");
    //         }
    //       } else {
    //         if (this.data.call.label === "intraday") {
    //           strategicOrderForm.controls.product.patchValue("MIS");
    //         } else {
    //           strategicOrderForm.controls.product.patchValue("NRML");
    //         }
    //       }
    //     }
    //     strategicOrderForm.controls.quantity.patchValue(
    //       calls.quantity * calls.lotSize
    //     );
    //     strategicOrderForm.controls.tradingSymbol.patchValue(
    //       calls.tradingSymbol
    //     );
    //     strategicOrderForm.controls.scripType.patchValue(calls.scriptType);
    //     strategicOrderForm.controls.price.patchValue(calls.currentPrice);
    //     strategicOrderForm.controls.callCategory.patchValue(calls.callCategory);
    //     strategicOrderForm.controls.id.patchValue(calls._id);
    //     this.strategyCalls.push(strategicOrderForm);
    //   }
    // } else {
    if (this.data.call.callCategory === "cash") {
      if (this.userBroker === "master_trust") {
        this.tradeCallForm.controls['product'].patchValue('NRML')
      } else {
        if (this.data.call.label === "intraday") {
          this.tradeCallForm.controls['product'].patchValue('MIS')
        } else {
          this.tradeCallForm.controls['product'].patchValue(this.data.call.callCategory === 'cash'? 'CNC': 'NRML')
        }
      }
    } else {
      this.tradeCallForm.controls.product.patchValue("NRML");
    }

    // if (this.data.call.scriptType === "BUY") {
    //   this.tradeCallForm.controls.price.patchValue(this.data.call.maxPurchasePriceRange);
    // } else if (this.data.call.scriptType === "SELL") {
    //   this.tradeCallForm.controls.price.patchValue(this.data.call.minPurchasePriceRange);
    // }

    // if(this.data.tradeData && this.data.tradeData._id){
    //   this.tradeCallForm.controls['quantity'].patchValue(this.data.tradeData.tradeQuantity)
    // }
    // }
  }

  goToProfile() {
    this.dialogRef.close(false)
    this.router.navigate(['/profile'])
  }

  entryPriceValidator(controls: FormControl) {
    if (controls && controls.value) {
      if (this.data.call.scriptType === 'BUY') {
        if (controls.value <= this.data.call.stopLoss) {
          return { price: 'Price cannot be less than or equal to stoploss' }
        } else if (controls.value >= this.data.call.targetOne) {
          return { price: 'Price cannot be greater than or equal to target' }
        }
      } else {
        if (controls.value >= this.data.call.stopLoss) {
          return { price: 'Price cannot be greater than or equal to stoploss' }
        } else if (controls.value <= this.data.call.targetOne) {
          return { price: 'Price cannot be less than or equal to target' }
        }
      }
    }
  }

   getVenturaOrders(orderData) {
   

    const call = { ...this.tradeCallForm.value }
    const newInstrument = {
      tradingsymbol: orderData.tradingSymbol,
      exchange: orderData.exchange,
      variety: 'regular',
      transaction_type: this.data.isExit ? 'SELL' : orderData.scriptType,
      order_type: call.orderType,
      product: orderData.tradeProductType,
      price: orderData.purchasePrice,
      quantity: orderData.quantity,
      readonly: false,
      tag: orderData.zerodhaTag,
    };
    let orders: any[] = [newInstrument];
    const appKey = this.venturaResponse?.ventura?.appKey;
    const clientId = this.venturaResponse?.ventura?.ventura?.clientId;
    const accessToken = this.venturaResponse?.ventura?.ventura?.accessToken;
    let basketIds = this.venturaResponse?.ventura?.basketIds;
    let currOrders: any = {
      orderId: orderData._id,
      type: 'trading'
    }

    console.log('bkssst',basketIds)
    if (!Array.isArray(basketIds)) {
     basketIds = basketIds ? [basketIds] : [];
    }

    if (!basketIds.length) {
      console.error('Ventura basketIds missing or invalid:', basketIds);
      this.alertService.error('Something went wrong. Please try again.');
      return;
    }

    window.localStorage.setItem('tradingOrder', JSON.stringify(currOrders))
    window.localStorage.setItem('OrderFor', 'trading')
    this.brokerageService.venturaOrderPlace(orders, appKey, clientId, accessToken, basketIds)
  }
}
